import { onSnapshot, query, orderBy, Timestamp, collection, where, documentId, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/FirebaseProvider';

export interface Topic {
  id?: string;
  topicValue: string;
  type?: 'USER' | 'COMMON';
  createdAt: Date;
  newsItemCount?: number;
  lastNewsItemAt?: Date;
  lastSearchedAt?: Date;
  clashCount?: number;
  attemptCount?: number;
  refreshTime?: number; // in minutes
  toBeSearchedAt?: Date;
  status?: 'ACTIVE' | 'PAUSED';
  subscriberCount?: number; // Number of users subscribed to this topic
}

export interface TopicData {
  topicValue: string;
  type?: 'USER' | 'COMMON';
  createdAt: Timestamp;
  newsItemCount?: number;
  lastNewsItemAt?: Timestamp;
  lastSearchedAt?: Timestamp;
  clashCount?: number;
  attemptCount?: number;
  refreshTime?: number; // in minutes
  toBeSearchedAt?: Timestamp;
  status?: 'ACTIVE' | 'PAUSED';
  subscriberCount?: number; // Number of users subscribed to this topic
}

// Add a topic using Firebase Function
export const addTopic = async (topicValue: string, type: 'USER' | 'COMMON' = 'USER', userId?: string): Promise<{success: boolean, topicId?: string, message: string}> => {
  try {
    const addTopicFunction = httpsCallable(functions, 'addTopic');
    const result = await addTopicFunction({ topicValue, type, userId });
    return result.data as {success: boolean, topicId?: string, message: string};
  } catch (error) {
    console.error('Error calling Firebase function:', error);
    throw error;
  }
};

// Update topic status using Firebase Function
export const updateTopicStatus = async (topicId: string, status: 'ACTIVE' | 'PAUSED'): Promise<void> => {
  try {
    const updateTopicStatusFunction = httpsCallable(functions, 'updateTopicStatus');
    await updateTopicStatusFunction({ topicId, status });
  } catch (error) {
    console.error('Error updating topic status:', error);
    throw error;
  }
};

// Update topic type using Firebase Function
export const updateTopicType = async (topicId: string, type: 'USER' | 'COMMON'): Promise<void> => {
  try {
    const updateTopicTypeFunction = httpsCallable(functions, 'updateTopicType');
    await updateTopicTypeFunction({ topicId, type });
  } catch (error) {
    console.error('Error updating topic type:', error);
    throw error;
  }
};

// Delete a topic using Firebase Function
export const deleteTopic = async (topicId: string): Promise<void> => {
  try {
    const deleteTopicFunction = httpsCallable(functions, 'deleteTopic');
    await deleteTopicFunction({ topicId });
  } catch (error) {
    console.error('Error deleting topic:', error);
    throw error;
  }
};

// Subscribe to a topic using Firebase Function
export const subscribeToTopic = async (topicId: string, userId: string): Promise<void> => {
  try {
    const subscribeFunction = httpsCallable(functions, 'subscribeToTopic');
    await subscribeFunction({ topicId, userId });
  } catch (error) {
    console.error('Error subscribing to topic:', error);
    throw error;
  }
};

// Unsubscribe from a topic using Firebase Function
export const unsubscribeFromTopic = async (topicId: string, userId: string): Promise<void> => {
  try {
    const unsubscribeFunction = httpsCallable(functions, 'unsubscribeFromTopic');
    await unsubscribeFunction({ topicId, userId });
  } catch (error) {
    console.error('Error unsubscribing from topic:', error);
    throw error;
  }
};

// Subscribe to topics in real-time
export const subscribeToTopics = (callback: (topics: Topic[]) => void) => {
  const q = query(collection(db, 'topics'), orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, (querySnapshot) => {
    const topics: Topic[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as TopicData;
      topics.push({
        id: doc.id,
        topicValue: data.topicValue,
        type: data.type || 'USER',
        createdAt: data.createdAt.toDate(),
        newsItemCount: data.newsItemCount || 0,
        lastNewsItemAt: data.lastNewsItemAt ? data.lastNewsItemAt.toDate() : undefined,
        lastSearchedAt: data.lastSearchedAt ? data.lastSearchedAt.toDate() : undefined,
        clashCount: data.clashCount || 0,
        attemptCount: data.attemptCount || 0,
        refreshTime: data.refreshTime || 60,
        toBeSearchedAt: data.toBeSearchedAt ? data.toBeSearchedAt.toDate() : undefined,
        status: data.status || 'ACTIVE',
        subscriberCount: data.subscriberCount || 0
      });
    });
    
    // Sort topics: 1. Never processed (attemptCount = 0), 2. Active, 3. Paused
    // Secondary sort by lastNewsItemAt (most recent news item first)
    topics.sort((a, b) => {
      const aAttempts = a.attemptCount || 0;
      const bAttempts = b.attemptCount || 0;
      const aStatus = a.status || 'ACTIVE';
      const bStatus = b.status || 'ACTIVE';
      
      // First priority: Never processed topics (attemptCount = 0) come first
      if (aAttempts === 0 && bAttempts > 0) return -1;
      if (aAttempts > 0 && bAttempts === 0) return 1;
      
      // If both have been processed, sort by status (Active before Paused)
      if (aAttempts > 0 && bAttempts > 0) {
        if (aStatus === 'ACTIVE' && bStatus === 'PAUSED') return -1;
        if (aStatus === 'PAUSED' && bStatus === 'ACTIVE') return 1;
      }
      
      // Secondary sort by lastNewsItemAt (most recent news item first)
      const aLastNewsItem = a.lastNewsItemAt?.getTime() || 0;
      const bLastNewsItem = b.lastNewsItemAt?.getTime() || 0;
      
      return bLastNewsItem - aLastNewsItem;
    });
    
    callback(topics);
  });
};

// Subscribe to user's subscribed topics in real-time
export const subscribeToUserTopics = (userId: string, callback: (topics: Topic[]) => void) => {
  // First get the user's subscribed topic IDs
  return onSnapshot(doc(db, 'users', userId), (userDoc) => {
    if (!userDoc.exists()) {
      callback([]);
      return;
    }
    
    const userData = userDoc.data();
    const subscribedTopicIds = userData.subscribedTopics || [];
    
    if (subscribedTopicIds.length === 0) {
      callback([]);
      return;
    }
    
    // Query topics where ID is in the subscribed list
    const q = query(
      collection(db, 'topics'), 
      where(documentId(), 'in', subscribedTopicIds),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (querySnapshot) => {
      const topics: Topic[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as TopicData;
        topics.push({
          id: doc.id,
          topicValue: data.topicValue,
          type: data.type || 'USER',
          createdAt: data.createdAt.toDate(),
          newsItemCount: data.newsItemCount || 0,
          lastNewsItemAt: data.lastNewsItemAt ? data.lastNewsItemAt.toDate() : undefined,
          lastSearchedAt: data.lastSearchedAt ? data.lastSearchedAt.toDate() : undefined,
          clashCount: data.clashCount || 0,
          attemptCount: data.attemptCount || 0,
          refreshTime: data.refreshTime || 60,
          toBeSearchedAt: data.toBeSearchedAt ? data.toBeSearchedAt.toDate() : undefined,
          status: data.status || 'ACTIVE',
          subscriberCount: data.subscriberCount || 0
        });
      });
      
      // Sort topics: 1. Never processed (attemptCount = 0), 2. Active, 3. Paused
      // Secondary sort by lastNewsItemAt (most recent news item first)
      topics.sort((a, b) => {
        const aAttempts = a.attemptCount || 0;
        const bAttempts = b.attemptCount || 0;
        const aStatus = a.status || 'ACTIVE';
        const bStatus = b.status || 'ACTIVE';
        
        // First priority: Never processed topics (attemptCount = 0) come first
        if (aAttempts === 0 && bAttempts > 0) return -1;
        if (aAttempts > 0 && bAttempts === 0) return 1;
        
        // If both have been processed, sort by status (Active before Paused)
        if (aAttempts > 0 && bAttempts > 0) {
          if (aStatus === 'ACTIVE' && bStatus === 'PAUSED') return -1;
          if (aStatus === 'PAUSED' && bStatus === 'ACTIVE') return 1;
        }
        
        // Secondary sort by lastNewsItemAt (most recent news item first)
        const aLastNewsItem = a.lastNewsItemAt?.getTime() || 0;
        const bLastNewsItem = b.lastNewsItemAt?.getTime() || 0;
        
        return bLastNewsItem - aLastNewsItem;
      });
      
      callback(topics);
    });
  });
};